import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Badge, Button, Dialog, Field, useToast } from './ui';

type Status = 'update' | 'unchanged' | 'not_found' | 'ambiguous' | 'conflict';

interface SyncRow {
  line: number;
  name: string;
  playerNumber: string;
  status: Status;
  detail: string;
}
interface SyncResult {
  dryRun: boolean;
  rows: SyncRow[];
  counts: Record<Status, number>;
}

const STATUS: Record<Status, { label: string; tone: 'success' | 'neutral' | 'warning' | 'danger' | 'info' }> = {
  update: { label: 'Wird ergänzt', tone: 'success' },
  unchanged: { label: 'Schon vollständig', tone: 'neutral' },
  conflict: { label: 'Bitte prüfen', tone: 'danger' },
  ambiguous: { label: 'Mehrdeutig', tone: 'warning' },
  not_found: { label: 'Nicht in der App', tone: 'info' },
};

/**
 * Spielerliste des Verbands abgleichen.
 *
 * Ergaenzt vorhandene Personen um Spielernummer und Geburtsdatum - legt aber
 * niemanden an: die Liste des Verbands enthaelt auch alle, die laengst nicht
 * mehr spielen. Erst pruefen, dann uebernehmen.
 */
export function PlayerSyncDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [dateiname, setDateiname] = useState('');
  const [bericht, setBericht] = useState<SyncResult | null>(null);
  const [alleZeigen, setAlleZeigen] = useState(false);

  const lauf = useMutation({
    mutationFn: (dryRun: boolean) => api.post<SyncResult>('/api/persons/player-sync', { csv, dryRun }),
    onSuccess: async (result) => {
      setBericht(result);
      if (!result.dryRun) {
        await qc.invalidateQueries({ queryKey: ['persons'] });
        await qc.invalidateQueries({ queryKey: ['person'] });
        toast.show(`${result.counts.update} Personen ergänzt`);
        schliessen();
      }
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Abgleich fehlgeschlagen', 'error'),
  });

  const schliessen = (): void => {
    setCsv('');
    setDateiname('');
    setBericht(null);
    setAlleZeigen(false);
    onClose();
  };

  const zeilen = bericht
    ? bericht.rows.filter((row) => alleZeigen || row.status !== 'not_found')
    : [];

  return (
    <Dialog
      open={open}
      onClose={schliessen}
      title="Verbandsliste abgleichen"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={schliessen}>Abbrechen</Button>
          <Button variant="secondary" loading={lauf.isPending && !bericht} disabled={!csv.trim()} onClick={() => lauf.mutate(true)}>
            Prüfen
          </Button>
          {bericht && bericht.counts.update > 0 && (
            <Button loading={lauf.isPending} onClick={() => lauf.mutate(false)}>
              {bericht.counts.update} ergänzen
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Ergänzt bei Personen, die es in der App schon gibt, die <strong>Spielernummer</strong> und das
          <strong> Geburtsdatum</strong>. Erkannt wird an der Spielernummer, sonst am Namen. Niemand wird
          neu angelegt, nichts Vorhandenes überschrieben – was nicht zusammenpasst, steht unter „Bitte prüfen“.
        </p>
        <p className="hint">Erwartete Spalten wie in der Liste des Verbands: Nr, Name, Vorname, Geburtsdat.</p>

        <Field label="Datei auswählen" hint={dateiname || 'CSV, mit Komma oder Semikolon getrennt'}>
          {(id) => (
            <input
              id={id}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="block w-full text-sm"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                setCsv(await file.text());
                setDateiname(file.name);
                setBericht(null);
              }}
            />
          )}
        </Field>

        {bericht && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-sm">
              {(Object.keys(STATUS) as Status[]).map((status) => (
                <Badge key={status} tone={STATUS[status].tone}>
                  {STATUS[status].label}: {bericht.counts[status]}
                </Badge>
              ))}
            </div>
            {bericht.counts.not_found > 0 && (
              <p className="hint">
                „Nicht in der App“ sind meist frühere Spielerinnen und Spieler – sie werden nicht angelegt.
                {' '}
                <button type="button" className="text-brand-700 hover:underline" onClick={() => setAlleZeigen((v) => !v)}>
                  {alleZeigen ? 'Ausblenden' : 'Anzeigen'}
                </button>
              </p>
            )}
            <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 text-sm">
              {zeilen.map((row) => (
                <li key={row.line} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <span className="min-w-0">
                    <span className="font-medium text-slate-800">{row.name}</span>
                    <span className="text-slate-500"> · Nr. {row.playerNumber}</span>
                    <span className="block text-xs text-slate-500">{row.detail}</span>
                  </span>
                  <Badge tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>
                </li>
              ))}
              {zeilen.length === 0 && <li className="px-3 py-2 text-slate-500">Keine Treffer unter den vorhandenen Personen.</li>}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
}
